import React, { useState, useEffect } from 'react';
import { FiPhone, FiMail, FiMapPin, FiClock } from 'react-icons/fi';
import API_BASE from '../config';
import usePageMeta from '../hooks/usePageMeta';

export default function ContactPage() {
    const [form, setForm] = useState({ name: '', phone: '', email: '', city: '', service: '', message: '' });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    usePageMeta(
        'Book a Free Interior Design Consultation | Bliv Design Studio',
        'Plan your dream home interiors with Bliv Design Studio. Book a free design consultation across South India for modular kitchens, wardrobes, and more.'
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const formData = new FormData();
            Object.keys(form).forEach(key => formData.append(key, form[key]));

            const response = await fetch(`${API_BASE}/contact.php`, {
                method: 'POST',
                body: formData
            });
            const data = await response.json();

            if (data.status === 'success') {
                setSubmitted(true);
            } else {
                setError(data.message || 'Something went wrong. Please try again.');
            }
        } catch (err) {
            setError('Failed to connect to the server.');
        } finally {
            setLoading(false);
        }
    };

    const contactInfo = [
        { icon: FiPhone, label: 'Call Us', value: '+91 9959365158 | +91 7330676456', href: 'tel:+919959365158' },
        { icon: FiMail, label: 'Email Us', value: 'Bhavana@blivdesigns.com', href: 'mailto:Bhavana@blivdesigns.com' },
        { icon: FiMapPin, label: 'Visit Us', value: 'Plot No 761, CBI Colony , Jubliee Hills, Road No - 39, Hyderabad, Telangana - 500033', href: '#' },
        { icon: FiClock, label: 'Working Hours', value: 'Mon–Sat: 9AM – 7PM', href: '#' },
    ];

    return (
        <>
            {/* Hero */}
            <section className="relative pt-36 pb-20 bg-[#0D0D0D]">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 text-center">
                    <p className="text-[#2E63E8] font-semibold uppercase tracking-widest text-sm mb-3">Get in Touch</p>
                    <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">Contact Us</h1>
                    <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                        Ready to transform your space? Get a free consultation with our expert designers today.
                    </p>
                </div>
            </section>

            {/* Contact Section */}
            <section className="section-pad bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                        {/* Info */}
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">Let's Create Something Beautiful Together</h2>
                            <p className="text-gray-600 mb-10 leading-relaxed">
                                Whether you're planning a complete home renovation or a single room makeover, we're here to help. Our team of expert designers will work with you every step of the way.
                            </p>
                            <div className="space-y-5 mb-10">
                                {contactInfo.map((item, i) => (
                                    <a href={item.href} key={i} className="flex items-start gap-4 group">
                                        <div className="w-12 h-12 bg-[#f0f4fe] rounded-xl flex items-center justify-center text-[#2E63E8] group-hover:bg-[#2E63E8] group-hover:text-white transition-all duration-300 flex-shrink-0">
                                            <item.icon size={20} />
                                        </div>
                                        <div>
                                            <p className="text-gray-500 text-xs uppercase tracking-widest font-semibold">{item.label}</p>
                                            <p className="text-gray-900 font-semibold mt-0.5">{item.value}</p>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>

                        {/* Form */}
                        <div className="bg-[#F8F9FA] rounded-3xl p-8 shadow-sm">
                            {submitted ? (
                                <div className="text-center py-16">
                                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                                        <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                                    <p className="text-gray-600">We've received your message and will contact you within 1 hour during business hours.</p>
                                </div>
                            ) : (
                                <>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Free Estimate</h3>
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <input type="text" placeholder="Full Name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#2E63E8] focus:ring-2 focus:ring-[#2E63E8]/20 outline-none bg-white transition-all" />
                                            <input type="tel" placeholder="Phone Number" required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#2E63E8] focus:ring-2 focus:ring-[#2E63E8]/20 outline-none bg-white transition-all" />
                                        </div>
                                        <input type="email" placeholder="Email Address" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#2E63E8] focus:ring-2 focus:ring-[#2E63E8]/20 outline-none bg-white transition-all" />
                                        <div className="grid grid-cols-2 gap-4">
                                            <input type="text" placeholder="Enter City" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#2E63E8] focus:ring-2 focus:ring-[#2E63E8]/20 outline-none bg-white transition-all shadow-sm" />
                                            <select value={form.service} onChange={e => setForm({ ...form, service: e.target.value })}
                                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-500 focus:border-[#2E63E8] outline-none bg-white transition-all">
                                                <option value="">Select Service</option>
                                                {['Modular Kitchen', 'Wardrobe', 'TV Unit', 'False Ceiling', 'Full Home Interior'].map(s => <option key={s}>{s}</option>)}
                                            </select>
                                        </div>
                                        <textarea placeholder="Tell us about your project..." rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#2E63E8] focus:ring-2 focus:ring-[#2E63E8]/20 outline-none bg-white transition-all resize-none" />
                                        <button type="submit" className="btn-yellow w-full py-4 text-base">
                                            Send Message & Get Free Estimate →
                                        </button>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
